import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum): every light in this piece is
// a light IN THE SCENE - sodium lamps, headlamps, windows, a dashboard. A stage
// rig over it would be a lamp in the car with you.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'nightdrive',
  family: 'photographic',
  danceability: 0.45,
  // 0, not 1 - requiresTier gates SELECTION, not capability, and the radio page
  // measures tier 0 on the hardware it actually runs on. Same reasoning as
  // swarm.js/nebula.js/maelstrom.js.
  requiresTier: 0,
  energyLevel: 0.42,
  bpmMin: 70,
  bpmMax: 140,
  movement: 0.55,
  tonality: 0.85,
  prefers: { dynamics: [0.25, 0.85] },
  enabled: true
};

// ============================================================================
// Nightdrive - a night road seen from the dashboard, rain on the windscreen,
// street lamps going past in time.
//
// THREE PLANES AT THREE SPEEDS, and that is the whole image: a windscreen that
// never moves, a road that pours past, a city that barely slides. Take away any
// one of them and it stops being a car.
//
// THE RHYTHM IS GEOMETRY, NOT AN EFFECT. One lane dash passes under the camera
// per beat and one lamp per bar - not approximately, by construction. The road
// carries a single scalar, uDist (how far the car has gone), and everything on
// it - dashes, lamps, guardrail - is a function of it. Locking the rhythm is
// therefore locking ONE number:
//
//   fract(uDist / LAMP_SPACING) == barPhase
//
// and because LAMP_SPACING is exactly four dash periods, the dashes come out
// locked to the beat for free. It is done with a phase-locked loop rather than
// by assignment (see _advance): assignment would make the road jump whenever
// the grid moved, and the one thing a travelling shot cannot do is jump. The
// loop also leaves room for the brief's phrase-level speed changes - the
// wobble rides on top and the loop quietly absorbs it, so the car visibly
// speeds up and slows down while a dash still crosses on the beat.
//
// THE ROAD IS NOT GEOMETRY. The brief asks for instanced geometry that scrolls;
// the road here is one full-screen quad whose fragment shader intersects the
// ground plane per pixel. It is one draw call instead of hundreds, the dashes
// are perspective-exact at any distance instead of popping as instances recycle,
// and - the reason that decided it - the wet reflection of the lamps needs to
// be evaluated at the GROUND POINT, which is exactly what this gives for free.
// The things that stand UP off the road, where a quad would be wrong, are
// instanced: lamps, guardrail, overpass.
//
// THE WET ROAD IS RAVESIGN'S REFLECTION, moved into perspective. There it was a
// mirrored copy of the geometry about a waterline, wobbled by two incommensurate
// sinusoids. Here the mirror is the road plane, and because the shader already
// knows the ground point it can be written directly: a lamp at distance dL
// smears toward the viewer, widening and dimming as it comes, with the same
// two-sinusoid wobble breaking it up. See lampWet().
//
// WHAT THE DROPS COST. They are the one thing that cannot be faked: refraction
// means sampling what is behind the glass, so the world is rendered into a
// target first and the windscreen composites it. That target is also what makes
// the drops, the dashboard glow and the artwork reflection share one image
// instead of three guesses at it.
// ============================================================================

// --- units --------------------------------------------------------------------
// 1 unit = 4 metres, so that the whole visible road fits inside the shared
// camera's far plane of 100 without this file having to touch it. Every
// constant below is written in units with its metric value beside it.
const M = 0.25;                       // units per metre

// --- the road -----------------------------------------------------------------
const DASH_PERIOD = 13 * M;           // 13 m: 3 m of line, 10 m of gap
const DASH_FILL = 3 / 13;
const LAMP_SPACING = DASH_PERIOD * 4; // 52 m - one per bar, four dashes
const ROAD_HALF = 5.0 * M;            // 5 m of tarmac either side of the line
const EDGE_LINE = 4.2 * M;
const LAMP_X = 6.8 * M;               // lamps stand off the shoulder
const LAMP_H = 8.0 * M;               // 8 m to the lamp head
const LAMP_ARM = 1.4 * M;             // the head hangs 1.4 m inboard of the post
const RAIL_X = 5.4 * M;               // the guardrail, right-hand side only
const RAIL_Y = 0.75 * M;
const CAM_H = 1.25 * M;               // eye height in a saloon car
// THE CAR DRIVES IN THE RIGHT-HAND LANE, not astride the centre line. The road
// is ten metres of tarmac with the dashes at x = 0, so the middle of the right
// lane is two and a half metres over. Everything that reads the camera - the
// lamps, the guardrail, the rain, the overpass - follows from uProj/uView and
// needs no change; only the road's own raymarch and the headlamps, which are
// written in the ROAD frame, have to be told where the car now is.
const CAR_X = 2.5 * M;
// Where the vanishing point sits, in ndc.y. Just above centre, which puts it
// under the cover - the road leads to the artwork, which is the framing the
// brief asks for.
const HORIZON_NDC = 0.10;
const FOG_FULL = 46;                  // 184 m: rain haze closes the road here
const LAMP_COUNT = 5;                 // pairs alive at once, the fog hides the rest
const RAIL_COUNT = 44;
const RAIL_SEG = 2.2 * M;

// --- speed ----------------------------------------------------------------------
// One dash per beat IS the speed: 13 m per beat is 78 km/h at 100 bpm, 108 at
// 140 and 55 at 70 - the whole of the brief's tempo range lands on speeds a car
// actually does, which is why this calibration and not another.
const NO_BPM = 100;                   // the brief's fixed fallback tempo
const PLL_TAU = 0.35;                 // how fast the road is pulled back on grid
const SPEED_WOBBLE = 0.06;            // +-6% over a phrase: audible as motion,
                                      // small enough for the loop to absorb
const STOP_SECONDS = 2.0;             // pause: the car rolls to a stop
const CURVE_MAX = 0.020;              // lateral bend, units per unit squared
// City-space units of lateral travel per unit of road on a dead straight road.
// The nearest layer then crosses the frame in about a minute and a half, and
// the furthest in seven - "glisse LENTEMENT", and different enough to read.
const CITY_DRIFT = 0.012;

// --- the wipers -----------------------------------------------------------------
const WIPER_BEATS = 4;                // one full sweep and return per bar
// Opposed blades, the way most saloons are wiped. Angles are measured from
// vertical, positive toward +x.
//
// THE PARK POSITION IS LYING DOWN, and getting its sign wrong is what a wiper
// looks like when it is wrong: at 0.55 rad the two blades pointed up and
// INWARD and met in the middle of the glass, so at rest - which is most of
// every bar - the frame carried a large black X across the road. Parked, a
// wiper lies along the bottom of the screen pointing outward, mostly behind
// the dashboard, and sweeps up and in from there.
const WIPER_PARK = 1.32;              // lying along the bottom, pointing out
const WIPER_SWEEP = 1.22;             // swept up and inward from there
// Long enough to reach y = 0.82 at the top of the sweep: a blade that only
// reached the middle of the glass cleared 14% of the drops in a bar, and a
// wiper that leaves most of the screen alone is not a wiper.
const WIPER_LEN = 2.00;               // height units, pivot to tip
const WIPER_INNER = 0.26;
const WIPER_PIVOT_X = 0.72;
const WIPER_PIVOT_Y = -1.18;
const DROP_REFRACT = 1.75;

// --- rain -------------------------------------------------------------------------
const RAIN_STREAKS = 220;
const RAIN_BOX = 26;                  // the volume ahead that carries rain

const TIER = {
  0: { drops: 0, cityLayers: 2, wetRoad: false, rain: 90, scale: 1.0 },
  1: { drops: 80, cityLayers: 2, wetRoad: true, rain: 150, scale: 1.0 },
  2: { drops: 150, cityLayers: 3, wetRoad: true, rain: RAIN_STREAKS, scale: 1.0 }
};
const DEFAULT_TIER = 2;
const SAFE_FLOOR = 0.5;

const DEFAULT_PALETTE = ['#ffd27f', '#ff6b8a', '#7fd4ff', '#b48cff'];

// ============================================================================
// WHAT WAS HERE: the photographic cabin, its speedometer, and the central
// screen that carried the cover and the title. All of it is gone - only the
// road, the windscreen and the wipers are left - and the cover and the title
// went back to the standard DOM overlay with it.
//
// One lesson from it is worth keeping, because it applies to anything that
// might later be tempted to move the glass: the cabin carried a continuous
// half-pixel tremble and a three-pixel pitch on every bar, and both had to go.
// The tremble made the photograph look like it was buzzing, and the pitch was
// an impulse - a step in velocity, and therefore a jolt no amount of damping
// hides.
// ============================================================================

const _scratchSize = new THREE.Vector2();

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(n) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(n);
}
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
// Shortest signed distance between two phases on the unit circle.
function phaseError(want, have) {
  let e = want - have;
  e -= Math.floor(e + 0.5);
  return e;
}

// ============================================================================
// Shared GLSL. Everything that more than one pass has to agree about lives
// here: if the road and the rain disagreed about where the headlamps point,
// the rain would light up outside the beam and the cabin would stop reading.
// ============================================================================
const HASH_CHUNK = `
  // Every input is wrapped into [0,1) before it is used. On the television
  // mediump is real fp16, and fract() of anything much over 16 has no fraction
  // left - the usual sin(dot(p, big)) idiom collapses into flat bands there.
  float h11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    return fract(p * (p + p));
  }
  float h21(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * 0.1031);
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x),
               mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
  }
`;

// The road's own frame: the lateral offset of the centreline at distance d.
// A constant-curvature bend, which is what a real road easement approximates
// and what lets the lamps and the guardrail follow the same curve as the
// tarmac without any of them knowing about each other.
const CURVE_CHUNK = `
  float roadCurve(float d, float k) { return k * d * d * 0.5; }
`;

// The headlamps. Two beams from under the bonnet, and the SAME function is
// read by the road, the rain and the guardrail - the rain is only visible
// where this is non-zero, which is what makes the beams look like beams
// rather than like a light painted on the tarmac.
const HEADLIGHT_CHUNK = `
  uniform float uHead;        // bands.low drives this
  // x lateral (road frame), d ahead, both in units of 4 m.
  //
  // TWO beams, not one: the lamps are 1.8 m apart, so near the bonnet there
  // are two pools that merge into one about fifteen metres out. It is the
  // single detail that stops the light reading as a spotlight under the car.
  // Calibrated to the brief's "une trentaine de metres": full at 16 m, gone
  // by 38, and about seven metres wide where it matters.
  float headlamp(float x, float d) {
    if (d <= 0.02) return 0.0;
    float spread = 0.30 + d * 0.20;
    // Centred on the CAR, which is a lane to the right of the dashes. Left at
    // +/- 0.225 about x = 0 the beams would light the centre line while the
    // car drove beside them.
    float cx = x - CAR_X_C;
    float l1 = exp(-((cx - 0.225) * (cx - 0.225)) / (spread * spread));
    float l2 = exp(-((cx + 0.225) * (cx + 0.225)) / (spread * spread));
    float lat = (l1 + l2) * 0.62;
    float near = smoothstep(0.0, 0.30, d);
    float far = 1.0 - smoothstep(4.0, 9.5, d);
    return lat * near * far * uHead;
  }
`;

// The wet road. A lamp is mirrored in the tarmac and the mirror image smears
// toward the viewer, widening and breaking up as it comes - two sinusoids of
// incommensurable period, exactly as ravesign.js wobbles its reflection, so
// the streak never repeats and never looks like a gradient.
const LAMP_CHUNK = `
  uniform float uDist;
  uniform float uCurve;
  uniform float uWet;
  uniform float uTime;
  uniform vec3 uSodium;
  uniform float uLampFlick;

  float lampDist(float k) { return k * LAMP_SPACING_C - mod(uDist, LAMP_SPACING_C); }

  // The pool of light the lamp actually throws on the tarmac, and the streak
  // its reflection drags toward the camera. Returned together because they
  // share the loop, the curve and the flicker.
  vec2 lampGround(float x, float d) {
    float pool = 0.0, wet = 0.0;
    for (int k = 0; k < LAMP_COUNT_C; k++) {
      float dl = lampDist(float(k));
      if (dl < -1.0) continue;
      float lx = roadCurve(dl, uCurve);
      float flick = 1.0 - uLampFlick * step(0.87, h11(float(k) + floor(uDist / LAMP_SPACING_C) * 0.37));
      for (int s = 0; s < 2; s++) {
        // The head hangs inboard of the post on its arm, and it is the head
        // that lights the road - putting the pool under the post instead
        // leaves the carriageway dark and the verge lit.
        float sx = (s == 0 ? -(LAMP_X_C - LAMP_ARM_C) : (LAMP_X_C - LAMP_ARM_C)) + lx;
        // The pool: an ellipse under the head, stretched along the road, and
        // stretched harder as the lamp comes at you - which is the one cue
        // that says the light is above the road and not painted on it.
        float dx = x - sx;
        float dd = d - dl;
        float stretch = 1.0 + 2.6 * exp(-max(dl, 0.0) * 0.16);
        float rr = (dx * dx) / (1.05 * 1.05) + (dd * dd) / (1.7 * 1.7 * stretch * stretch);
        pool += exp(-rr) * flick;
        // The reflection: only between the lamp and the camera, widening and
        // dimming the whole way, with the surface broken into ripples.
        float behind = dl - d;
        if (behind > -0.2) {
          float w = sin(d * 9.3 + uTime * 1.7 + float(k)) * 0.55
                  + sin(d * 5.1 - uTime * 1.13) * 0.45;
          float wob = w * 0.085 * clamp(behind * 0.8, 0.0, 1.0);
          float lat = abs(dx + wob) / (0.16 + behind * 0.075);
          wet += exp(-lat * lat) * exp(-behind * 0.30) * flick;
        }
      }
    }
    return vec2(pool, wet * uWet);
  }
`;

// Distance haze. A rainy night has no far distance at all, and this is also
// what hides the point where the instanced lamps and guardrail run out.
const FOG_CHUNK = `
  float fogAt(float d) { return 1.0 - exp(-d * (2.4 / FOG_FULL_C)); }
`;

function bake(src, extra) {
  let s = src
    .replace(/LAMP_SPACING_C/g, LAMP_SPACING.toFixed(5))
    .replace(/LAMP_COUNT_C/g, String(LAMP_COUNT))
    .replace(/LAMP_X_C/g, LAMP_X.toFixed(5))
    .replace(/LAMP_H_C/g, LAMP_H.toFixed(5))
    .replace(/LAMP_ARM_C/g, LAMP_ARM.toFixed(5))
    .replace(/DASH_PERIOD_C/g, DASH_PERIOD.toFixed(5))
    .replace(/DASH_FILL_C/g, DASH_FILL.toFixed(4))
    .replace(/ROAD_HALF_C/g, ROAD_HALF.toFixed(4))
    .replace(/EDGE_LINE_C/g, EDGE_LINE.toFixed(4))
    .replace(/RAIL_X_C/g, RAIL_X.toFixed(4))
    .replace(/RAIL_Y_C/g, RAIL_Y.toFixed(4))
    .replace(/CAM_H_C/g, CAM_H.toFixed(5))
    .replace(/CAR_X_C/g, CAR_X.toFixed(5))
    .replace(/FOG_FULL_C/g, FOG_FULL.toFixed(1))
    .replace(/SAFE_FLOOR_C/g, SAFE_FLOOR.toFixed(2))
    .replace(/WIPER_INNER_C/g, WIPER_INNER.toFixed(3))
    .replace(/WIPER_LEN_C/g, WIPER_LEN.toFixed(3))
    .replace(/RAIL_SEG_C/g, RAIL_SEG.toFixed(5))
    .replace(/RAIN_BOX_C/g, RAIN_BOX.toFixed(1));
  if (extra) for (const k in extra) s = s.replace(new RegExp(k, 'g'), extra[k]);
  return s;
}

// A quad that covers the frame whatever the camera is doing. position.xy comes
// from a PlaneGeometry(1,1), so [-0.5,0.5] doubled is exactly the clip cube.
const FULLSCREEN_VS = `
  precision mediump float;
  varying vec2 vUv;
  varying vec2 vNdc;
  void main() {
    vUv = uv;
    vNdc = position.xy * 2.0;
    gl_Position = vec4(position.xy * 2.0, 0.999, 1.0);
  }
`;

// ============================================================================
// SKY AND CITY. One pass, three parallax layers plus the light pollution over
// them. Drawn first, so the road quad and everything instanced sits on top.
// ============================================================================
const SKY_FS = `
  precision mediump float;
  varying vec2 vNdc;

  uniform float uHorizon;
  uniform float uAspect;
  uniform float uLateral;
  uniform float uSeed;
  uniform float uCityDensity;
  uniform int uOnlyLayer;
  uniform float uHigh;
  uniform float uTime;
  uniform vec3 uPal0;
  uniform vec3 uPal1;
  uniform vec3 uPal2;
  uniform vec3 uSodium;
  uniform int uLayers;

  ${HASH_CHUNK}

  // One layer of skyline. cx is the city coordinate, already scrolled and
  // scaled for this layer's distance; y is height above the horizon in the
  // same units. Returns coverage in .x and window light in .y.
  vec2 layer(float cx, float y, float seed, float scale, float lit) {
    float slot = floor(cx);
    float f = fract(cx);
    // Each slot holds one building of random width and height, with a gap.
    float w = 0.45 + 0.5 * h11(slot * 1.7 + seed);
    float h = (0.18 + 0.82 * h11(slot * 3.1 + seed + 11.0));
    h *= h;                                  // more low blocks than towers
    h = (0.10 + 0.95 * h) * scale;
    if (f > w) return vec2(0.0);
    if (y > h || y < 0.0) return vec2(0.0);
    // Windows: a coarse grid inside the block. Density rises with progress,
    // so the skyline goes from a few lit windows out of town to a full centre.
    vec2 cell = vec2(floor((f / w) * 6.0), floor((y / h) * 16.0));
    float r = h21(cell + vec2(slot * 13.0, seed * 7.0));
    float on = step(1.0 - lit, r);
    // A handful flicker: an aerial light, a failing sign.
    float fl = step(0.965, h21(cell.yx + seed));
    on *= mix(1.0, 0.35 + 0.65 * abs(sin(uTime * (2.0 + 6.0 * r) + r * 30.0)), fl);
    // Keep the window off the very edge of its block, or the silhouette
    // dissolves and the buildings stop having outlines.
    vec2 inCell = fract(vec2((f / w) * 6.0, (y / h) * 16.0));
    float shape = step(0.30, inCell.x) * step(inCell.x, 0.70)
                * step(0.34, inCell.y) * step(inCell.y, 0.66);
    // A wide spread of window brightness, or every lit pane reads as the same
    // lamp and the block becomes a bar chart.
    float bright = 0.12 + 1.5 * r * r;
    return vec2(1.0, on * shape * bright);
  }

  void main() {
    float y = vNdc.y - uHorizon;
    // Night sky: cold above, warmed by the city's own glow at the horizon.
    vec3 col = mix(vec3(0.012, 0.016, 0.030), vec3(0.030, 0.028, 0.038),
                   clamp(1.0 - y * 1.6, 0.0, 1.0));
    // Light pollution: a wide, warm dome sitting on the skyline. It is the
    // thing that says "there is a city there" before a single window does.
    float halo = exp(-max(y, 0.0) * 5.2) * (0.35 + 0.5 * uCityDensity);
    col += uSodium * halo * 0.16;

    if (y > 0.0) {
      float sx = vNdc.x * uAspect;
      // Distances 1 : 2 : 5, so the layers scroll at 1, 0.5 and 0.2 times the
      // road - the brief's ratios, expressed as the parallax that produces them.
      vec3 pal[3];
      pal[0] = uPal0; pal[1] = uPal1; pal[2] = uPal2;
      for (int i = 0; i < 3; i++) {
        if (i >= uLayers) break;
        if (uOnlyLayer >= 0 && i != uOnlyLayer) continue;
        float far = (i == 0) ? 1.0 : ((i == 1) ? 2.0 : 5.0);
        // PARALLAX IS A LATERAL DISPLACEMENT, NOT A FORWARD ONE. Driving at a
        // skyline in a straight line does not slide it sideways at all - it
        // looms. What slides it is the road bending, and a feature at distance
        // D then moves by (lateral travel)/D, which is where the brief's
        // 1 : 0.5 : 0.2 comes from.
        //
        // The trap this started in: cx was scaled by 1/far AND scrolled by
        // 1/far, and the two cancelled exactly - all three layers moved across
        // the screen at the same rate, which is not parallax, it is wallpaper.
        // Buildings look SMALLER further away, so the city-space scale grows
        // with distance, and the scroll term is then the SAME for every layer.
        float scale = 0.30 / far + 0.06;
        float cx = sx * (1.7 * far) + uLateral + uSeed * (7.3 + float(i));
        vec2 b = layer(cx, y, uSeed + float(i) * 31.0, scale, uCityDensity * (1.0 - 0.18 * float(i)));
        if (b.x > 0.5) {
          // The block itself: almost black, a touch bluer further away.
          vec3 blk = mix(vec3(0.010, 0.012, 0.022), vec3(0.020, 0.022, 0.034), float(i) / 3.0);
          vec3 win = pal[i] * b.y * (1.0 + 0.8 * uHigh);
          // Quieter than the road, and further layers quieter still - a city
          // on the horizon is a backdrop, and at full strength it took the eye
          // straight off the thing the piece is actually about.
          col = blk + win * (0.46 - 0.10 * float(i));
        }
      }
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ============================================================================
// THE ROAD. A full-screen quad that intersects the ground plane per pixel.
// ============================================================================
const ROAD_FS = `
  precision mediump float;
  varying vec2 vNdc;

  uniform vec2 uTan;
  uniform mat3 uCamRot;
  uniform vec3 uCamPos;
  uniform float uRoadWet;
  uniform float uGrain;

  ${HASH_CHUNK}
  ${CURVE_CHUNK}
  ${HEADLIGHT_CHUNK}
  ${LAMP_CHUNK}
  ${FOG_CHUNK}

  void main() {
    vec3 rd = normalize(uCamRot * vec3(vNdc.x * uTan.x, vNdc.y * uTan.y, -1.0));
    // Above the horizon there is no road - the city behind shows through.
    if (rd.y > -0.0012) discard;
    float t = -uCamPos.y / rd.y;
    vec3 p = uCamPos + rd * t;
    float d = -p.z;
    if (d <= 0.02 || d > FOG_FULL_C * 1.6) discard;
    float x = p.x - roadCurve(d, uCurve);

    // --- asphalt --------------------------------------------------------------
    // Two octaves at very different scales: chippings, and the wide patches
    // where the surface holds water. Sampled in ROAD coordinates, so the
    // texture travels with the road rather than swimming on the screen.
    float s = uDist + d;
    float grit = vnoise(vec2(x * 26.0, s * 26.0)) * 0.6 + vnoise(vec2(x * 7.0, s * 6.0)) * 0.4;
    float sheet = smoothstep(0.42, 0.72, vnoise(vec2(x * 1.7, s * 0.9)));
    float wetness = uRoadWet * (0.35 + 0.65 * sheet);
    vec3 tar = mix(vec3(0.030, 0.031, 0.036), vec3(0.016, 0.017, 0.024), sheet);
    tar *= 0.75 + 0.5 * grit;

    // --- the markings ---------------------------------------------------------
    // The centre line is the metronome: one dash per DASH_PERIOD of road, and
    // uDist is locked so that a dash centre crosses the camera on the beat.
    float dashPhase = fract(s / DASH_PERIOD_C);
    float dash = step(dashPhase, DASH_FILL_C) * (1.0 - smoothstep(0.05, 0.085, abs(x)));
    float edge = (1.0 - smoothstep(0.022, 0.040, abs(abs(x) - EDGE_LINE_C)));
    float paint = clamp(dash + edge * 0.85, 0.0, 1.0);
    // Worn paint, or it reads as a decal rather than as road marking.
    paint *= 0.72 + 0.28 * vnoise(vec2(x * 30.0, s * 18.0));

    // --- light on the tarmac ---------------------------------------------------
    float beam = headlamp(x, d);
    vec2 lamp = lampGround(x, d);
    float lit = beam + lamp.x * 0.85;

    // Re-exposed when the cabin was here: its opening put the horizon at
    // the top third, so the camera looks down about 18 degrees and the near
    // carriageway fills far more of the frame than it did under the procedural
    // dashboard - at the old gain of 3.4 the headlit tarmac clipped to white.
    vec3 col = tar * (0.04 + lit * 1.75);
    // Paint is retro-reflective: it answers the headlamps far more than the
    // tarmac does, which is exactly why lane markings are visible at night.
    // Retro-reflective paint answers the headlamps far more than tarmac does,
    // which is why lane markings are what you actually see at night - but not
    // so much more that a near dash clips to white, which it did at 2.35.
    col += vec3(0.88, 0.88, 0.83) * paint * (0.015 + beam * 0.52 + lamp.x * 0.28);
    // The sodium reflection, smeared toward the viewer.
    col += uSodium * lamp.y * (0.32 + 0.95 * wetness);
    // Wet tarmac also mirrors the sky's own glow as a soft vertical sheen.
    col += uSodium * wetness * 0.030 * (1.0 - smoothstep(0.0, 18.0, d));

    float fog = fogAt(d);
    col = mix(col, vec3(0.014, 0.016, 0.026), fog);
    col += (h21(gl_FragCoord.xy) - 0.5) * uGrain;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ============================================================================
// THE LAMPS. Instanced: five pairs, each drawn as a post, a head and the cone
// of light under it. Their distance is a function of uDist and the instance
// index, so nothing is uploaded when the road moves.
// ============================================================================
const LAMP_VS = `
  precision mediump float;
  attribute float aSide;      // -1 left, +1 right
  attribute float aIndex;     // which pair
  attribute float aPart;      // 0 post, 1 head, 2 cone

  uniform float uDist;
  uniform float uCurve;
  uniform float uLampFlick;
  uniform mat4 uProj;
  uniform mat4 uView;

  varying float vPart;
  varying vec2 vLocal;
  varying float vFog;
  varying float vFlick;

  ${HASH_CHUNK}
  ${CURVE_CHUNK}
  ${FOG_CHUNK}

  void main() {
    float dl = aIndex * LAMP_SPACING_C - mod(uDist, LAMP_SPACING_C);
    vPart = aPart;
    vLocal = uv;
    vFog = 1.0 - fogAt(max(dl, 0.0));
    vFlick = 1.0 - uLampFlick * step(0.87, h11(aIndex + floor(uDist / LAMP_SPACING_C) * 0.37));
    // Behind the camera, or past the haze: collapse it rather than clipping it,
    // which keeps the draw call at a fixed size and costs one vertex.
    if (dl < -1.0 || dl > FOG_FULL_C * 1.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float lx = roadCurve(dl, uCurve) + aSide * LAMP_X_C;
    // position.xy is the part's own quad, x already signed by the arm's reach.
    vec3 world = vec3(lx + position.x * -aSide, position.y, -dl);
    gl_Position = uProj * uView * vec4(world, 1.0);
  }
`;
const LAMP_FS = `
  precision mediump float;
  uniform vec3 uSodium;
  uniform float uWet;
  varying float vPart;
  varying vec2 vLocal;
  varying float vFog;
  varying float vFlick;
  void main() {
    vec2 c = vLocal * 2.0 - 1.0;
    if (vPart < 0.5) {
      // The column, lit by its own lamp: the light spills down the pole and
      // fades toward the ground. A pure silhouette would be invisible against
      // a night sky, which is the one thing it cannot be.
      float down = smoothstep(0.0, 0.75, vLocal.y);
      gl_FragColor = vec4(uSodium * (0.035 + 0.14 * down) * vFog * vFlick, 1.0);
    } else if (vPart < 1.5) {
      // The head: a small hard source inside a wide soft one.
      float r = length(c);
      float core = 1.0 - smoothstep(0.0, 0.34, r);
      float halo = pow(max(0.0, 1.0 - r), 2.0);
      vec3 col = mix(uSodium, vec3(1.0), core * 0.7) * (core * 2.6 + halo * 1.5);
      gl_FragColor = vec4(col * vFog * vFlick, 1.0);
    } else {
      // The cone: light in the rain, brightest at the head and gone before it
      // reaches the tarmac, where the road shader takes over with the pool.
      float down = 1.0 - vLocal.y;
      float across = 1.0 - abs(c.x);
      float a = pow(max(across, 0.0), 1.6) * pow(down, 1.4) * 0.85;
      gl_FragColor = vec4(uSodium * a * vFog * vFlick, 1.0);
    }
  }
`;

// ============================================================================
// THE GUARDRAIL. Right-hand side only, in segments, with a reflector on every
// third post - the thing that actually answers the headlamps on a dark road.
// ============================================================================
const RAIL_VS = `
  precision mediump float;
  attribute float aIndex;
  attribute float aPart;      // 0 rail band, 1 reflector
  uniform float uDist;
  uniform float uCurve;
  uniform mat4 uProj;
  uniform mat4 uView;
  varying float vPart;
  varying float vFog;
  varying float vBeam;
  varying vec2 vLocal;
  ${HASH_CHUNK}
  ${CURVE_CHUNK}
  ${HEADLIGHT_CHUNK}
  ${FOG_CHUNK}
  void main() {
    float seg = RAIL_SEG_C;
    float dl = aIndex * seg - mod(uDist, seg);
    vPart = aPart;
    vLocal = uv;
    vFog = 1.0 - fogAt(max(dl, 0.0));
    vBeam = headlamp(RAIL_X_C, max(dl, 0.05));
    if (dl < -0.5 || dl > FOG_FULL_C) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float lx = roadCurve(dl, uCurve) + RAIL_X_C;
    vec3 world = vec3(lx, position.y, -(dl + position.x * seg));
    gl_Position = uProj * uView * vec4(world, 1.0);
  }
`;
const RAIL_FS = `
  precision mediump float;
  varying float vPart;
  varying float vFog;
  varying float vBeam;
  varying vec2 vLocal;
  void main() {
    if (vPart < 0.5) {
      // Galvanised steel: dark, with a bright lip where the beam grazes it.
      // RECALIBRATED FOR THE RIGHT-HAND LANE. These gains were set when the car
      // straddled the centre line and the rail sat 1.35 units out, far into the
      // beam's tail; from the right-hand lane it is 0.72 out, which is inside
      // the beam, and the same numbers turned the whole guardrail into a solid
      // white streak - measured at 0.381 mean luminance where the road beside
      // it was 0.065.
      float lip = smoothstep(0.55, 0.95, vLocal.y);
      float b = 0.055 + vBeam * (0.20 + 0.80 * lip);
      gl_FragColor = vec4(vec3(b, b * 1.0, b * 0.96) * vFog, 1.0);
    } else {
      float f = 1.0 - abs(vLocal.x * 2.0 - 1.0);
      gl_FragColor = vec4(vec3(1.0, 0.72, 0.30) * (0.05 + vBeam * 1.8) * f * vFog, 1.0);
    }
  }
`;

// ============================================================================
// THE RAIN. Short streaks in the volume ahead, leaning back with the speed,
// and only ever visible where a headlamp or a lamp pool puts light in the air -
// which is the difference between rain and a screen of white dashes.
// ============================================================================
const RAIN_VS = `
  precision mediump float;
  attribute float aSeed;
  attribute float aEnd;       // 0 head of the streak, 1 tail
  // uDist, uCurve, uTime, uSodium and uLampFlick all arrive with LAMP_CHUNK
  // below - declaring them here as well is a redefinition, not an override.
  uniform float uRain;
  uniform float uSpeed;
  uniform mat4 uProj;
  uniform mat4 uView;
  varying float vLit;
  ${HASH_CHUNK}
  ${CURVE_CHUNK}
  ${HEADLIGHT_CHUNK}
  ${LAMP_CHUNK}
  void main() {
    // Analytic, wrapped, bounded: the whole field is three fract()s of values
    // that never leave [0,1), so nothing drifts and nothing overflows fp16.
    float k = aSeed;
    float d = fract(h11(k * 1.7) + uTime * 0.021) * RAIN_BOX_C;
    float x = (h11(k * 3.1) - 0.5) * 2.6;
    float fall = 2.6 + 2.2 * h11(k * 5.3);
    float y = fract(h11(k * 7.9) - uTime * fall * 0.22) * 1.6;
    // Alive only for as much of the field as the density asks for.
    if (h11(k * 11.3) > uRain) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float lx = roadCurve(d, uCurve);
    // The streak leans back with the car's own speed: the faster it goes, the
    // more the rain reads as horizontal.
    float len = 0.030 + 0.055 * h11(k * 13.7);
    vec3 head = vec3(x + lx, y, -d);
    vec3 tail = head + vec3(0.0, len * 1.4, -len * uSpeed * 0.75);
    vec3 world = mix(head, tail, aEnd);
    float beam = headlamp(x, d);
    vec2 lamp = lampGround(x + lx, d);
    vLit = (beam * 1.5 + lamp.x * 0.55) * (1.0 - aEnd * 0.75);
    gl_Position = uProj * uView * vec4(world, 1.0);
  }
`;
const RAIN_FS = `
  precision mediump float;
  varying float vLit;
  void main() {
    if (vLit <= 0.004) discard;
    gl_FragColor = vec4(vec3(0.78, 0.83, 0.95) * vLit * 0.55, 1.0);
  }
`;

// ============================================================================
// THE OVERPASS. One quad across the road, rare, on major phrases.
// ============================================================================
const OVER_VS = `
  precision mediump float;
  uniform float uDist;
  uniform float uOverAt;
  uniform float uCurve;
  uniform mat4 uProj;
  uniform mat4 uView;
  varying vec2 vLocal;
  varying float vFog;
  ${CURVE_CHUNK}
  ${FOG_CHUNK}
  void main() {
    float dl = uOverAt - uDist;
    vLocal = uv;
    vFog = 1.0 - fogAt(max(dl, 0.0));
    if (dl < -2.0 || dl > FOG_FULL_C) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 world = vec3(position.x * 9.0 + roadCurve(dl, uCurve), 1.05 + position.y * 0.85, -dl);
    gl_Position = uProj * uView * vec4(world, 1.0);
  }
`;
const OVER_FS = `
  precision mediump float;
  uniform vec3 uSodium;
  varying vec2 vLocal;
  varying float vFog;
  void main() {
    // A concrete deck seen from underneath: black, with the sodium of its own
    // soffit lights caught along the bottom lip.
    float lip = 1.0 - smoothstep(0.0, 0.16, vLocal.y);
    vec3 col = vec3(0.016, 0.017, 0.024) + uSodium * lip * 0.22;
    gl_FragColor = vec4(col * vFog, 1.0);
  }
`;

// ============================================================================
// THE WINDSCREEN. The world target, seen through glass: a tint, the film of
// water the wipers keep clearing. Without this layer the piece is a road.
// With it, you are behind a windscreen looking at one.
// ============================================================================
const GLASS_FS = `
  precision mediump float;
  varying vec2 vUv;
  varying vec2 vNdc;

  uniform sampler2D uScene;
  uniform float uAspect;
  uniform vec2 uWiperAngle;
  uniform vec2 uPivotL;
  uniform vec2 uPivotR;
  uniform float uFilm;
  uniform float uTime;

  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  ${HASH_CHUNK}

  // How long ago the blade passed this point, in radians of arc. 0 right
  // behind the blade, growing the further back you look - which is exactly
  // the water reforming.
  float sinceWipe(vec2 hu, vec2 pivot, float angle) {
    vec2 d = hu - pivot;
    float r = length(d);
    if (r < WIPER_INNER_C || r > WIPER_LEN_C) return 9.0;
    float a = atan(d.x, d.y);
    float diff = a - angle;
    return abs(diff);
  }

  void main() {
    vec2 hu = vec2(vNdc.x * uAspect, vNdc.y);
    vec3 scene = texture2D(uScene, vUv).rgb;

    // --- the glass ------------------------------------------------------------
    // A faint cool tint and a little loss, so the world outside is never quite
    // as clean as the lights inside.
    scene *= vec3(0.94, 0.97, 1.02);
    // Tinted strip along the top, as every windscreen has.
    scene *= mix(0.35, 1.0, smoothstep(0.62, 0.30, vNdc.y));

    // --- the water film -------------------------------------------------------
    float age = min(sinceWipe(hu, uPivotL, uWiperAngle.x), sinceWipe(hu, uPivotR, uWiperAngle.y));
    float film = uFilm * smoothstep(0.02, 0.85, age);
    // The film scatters: a directional smear plus a slight lift of the blacks,
    // which is what a wet screen actually does to oncoming light.
    float smear = vnoise(vec2(hu.x * 26.0, hu.y * 5.0 - uTime * 0.6));
    vec3 blur = texture2D(uScene, vUv + vec2(0.0, 0.004 * film)).rgb
              + texture2D(uScene, vUv - vec2(0.0, 0.004 * film)).rgb;
    scene = mix(scene, blur * 0.5, film * 0.55);
    scene += scene * film * smear * 0.35;

    // The instruments' amber wash climbing off the bottom of the opening, the
    // dashboard reflected on the inside of the glass, and the blue of the
    // navigation screen bouncing off it were all here. All three were light
    // cast by the cabin, and the cabin is gone: there is nothing behind the
    // glass now but the driver.

    // --- framing --------------------------------------------------------------
    float vig = 1.0 - 0.45 * smoothstep(0.55, 1.45, length(hu * vec2(0.75, 1.0)));
    scene *= vig;
    vec2 qs = abs(hu - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(qs, 0.0)) + min(max(qs.x, qs.y), 0.0);
    scene *= mix(SAFE_FLOOR_C, 1.0, smoothstep(0.0, uSafeFeather, sd));

    scene += (h21(gl_FragCoord.xy + uTime) - 0.5) * (1.5 / 255.0);
    gl_FragColor = vec4(scene, 1.0);
  }
`;

// ============================================================================
// THE DROPS. Instanced quads that sample the world target behind them with an
// inverted, magnified offset - a drop is a lens, and a lens turns the image
// over. The head is a circle; the tail is the trail it has dragged up the
// glass. Positions come from the CPU because the wipers have to be able to
// delete them, which is criterion 4 and cannot be done analytically.
// ============================================================================
const DROP_VS = `
  precision mediump float;
  attribute vec2 aPos;        // height units
  attribute float aSize;
  attribute float aTrail;
  attribute float aFade;
  uniform float uAspect;
  uniform vec2 uCover;
  varying vec2 vLocal;
  varying float vTrail;
  varying float vFade;
  varying vec2 vCentreUv;
  varying float vRadUv;
  varying vec2 vImg;
  void main() {
    float tl = aTrail * 7.0;
    vec2 q = position.xy;
    float ly = mix(-(1.0 + tl), 1.0, q.y + 0.5);
    vLocal = vec2(q.x * 2.0, ly);
    vTrail = tl;
    vFade = aFade;
    vec2 hu = aPos + vLocal * aSize;
    vec2 ndc = vec2(hu.x / uAspect, hu.y);
    vCentreUv = vec2(aPos.x / uAspect, aPos.y) * 0.5 + 0.5;
    vRadUv = aSize * 0.5;
    vImg = (vec2(vCentreUv.x, 1.0 - vCentreUv.y) - 0.5) * uCover + 0.5;
    gl_Position = vec4(ndc, 0.0, 1.0);
  }
`;
const DROP_FS = `
  precision mediump float;
  uniform sampler2D uScene;
  uniform sampler2D uMask;
  uniform float uHasMask;
  uniform float uAspect;
  uniform float uRefract;
  varying vec2 vLocal;
  varying float vTrail;
  varying float vFade;
  varying vec2 vCentreUv;
  varying float vRadUv;
  varying vec2 vImg;
  void main() {
    float r = length(vLocal);
    // The head, and the tail under it.
    float head = 1.0 - smoothstep(0.80, 1.0, r);
    float tw = 0.42 * clamp(1.0 + vLocal.y / max(vTrail, 0.001), 0.0, 1.0);
    float tail = (vLocal.y < 0.0 && vTrail > 0.01)
      ? (1.0 - smoothstep(tw * 0.55, tw, abs(vLocal.x))) * (1.0 + vLocal.y / max(vTrail, 0.001))
      : 0.0;
    float a = max(head, tail * 0.55) * vFade;
    // Water is on the WINDSCREEN, so it stops at the edge of the opening. The
    // The mask was white on the cabin, so the glass was one minus it. With no
    // cabin uHasMask is always 0 and this branch never runs; it is left because
    // the windscreen is still a shape a future cut-out could clip against.
    if (uHasMask > 0.5) {
      float inside = (vImg.x < 0.0 || vImg.x > 1.0 || vImg.y < 0.0 || vImg.y > 1.0)
        ? 0.0 : (1.0 - texture2D(uMask, vImg).r);
      a *= smoothstep(0.25, 0.75, inside);
    }
    if (a <= 0.008) discard;

    // Refraction: invert and magnify. The 1/sqrt term is the lens getting
    // steeper toward the rim, which is what makes the edge of a drop bend the
    // light so much more than its middle.
    float bulge = 1.0 / sqrt(max(1.0 - r * r * 0.72, 0.15));
    vec2 off = -vLocal * vRadUv * uRefract * bulge;
    off.x /= uAspect;
    vec2 uv = clamp(vCentreUv + off * 0.5, 0.002, 0.998);
    vec3 col = texture2D(uScene, uv).rgb;
    // A drop concentrates light: what is behind it arrives brighter, and the
    // rim catches a highlight of its own.
    // A drop only shows where there is light behind it to bend; a rim bright
    // enough to see on its own turns the screen into a sheet of bubbles.
    col *= 1.0 + head * 0.32;
    float rim = smoothstep(0.72, 0.99, r) * head;
    col += vec3(0.55, 0.62, 0.75) * rim * 0.09;
    gl_FragColor = vec4(col, a);
  }
`;

// ============================================================================
// THE WIPERS. Two blades, one full sweep and return per bar. Drawn after the
// drops, because a blade goes over the water rather than under it.
// ============================================================================
const WIPER_VS = `
  precision mediump float;
  attribute float aWiper;
  uniform vec2 uWiperAngle;
  uniform vec2 uPivotL;
  uniform vec2 uPivotR;
  uniform float uAspect;
  uniform vec2 uCover;
  varying vec2 vLocal;
  varying vec2 vImg;
  void main() {
    float a = aWiper < 0.5 ? uWiperAngle.x : uWiperAngle.y;
    vec2 pivot = aWiper < 0.5 ? uPivotL : uPivotR;
    float rad = mix(WIPER_INNER_C, WIPER_LEN_C, position.y + 0.5);
    float lat = position.x * 0.085;
    vLocal = vec2(position.x * 2.0, position.y + 0.5);
    vec2 dir = vec2(sin(a), cos(a));
    vec2 nrm = vec2(cos(a), -sin(a));
    vec2 hu = pivot + dir * rad + nrm * lat;
    vec2 ndc = vec2(hu.x / uAspect, hu.y);
    vec2 suv = ndc * 0.5 + 0.5;
    vImg = (vec2(suv.x, 1.0 - suv.y) - 0.5) * uCover + 0.5;
    gl_Position = vec4(ndc, 0.0, 1.0);
  }
`;
const WIPER_FS = `
  precision mediump float;
  uniform sampler2D uMask;
  uniform float uHasMask;
  varying vec2 vLocal;
  varying vec2 vImg;
  void main() {
    float x = abs(vLocal.x);
    // The rubber, and the shadow it throws on the road behind it - the soft
    // wide part, which is what makes the blade sit in front of the world
    // rather than on top of the picture.
    float blade = 1.0 - smoothstep(0.10, 0.22, x);
    float shade = (1.0 - smoothstep(0.22, 0.75, x)) * 0.14;
    float a = clamp(blade * 0.94 + shade, 0.0, 1.0);
    // The arm stops short of the very tip.
    a *= smoothstep(0.0, 0.06, vLocal.y) * (1.0 - smoothstep(0.96, 1.0, vLocal.y));
    // The blade used to be cut off where the dashboard began; with no dashboard
    // it simply runs to the bottom of the frame.
    if (uHasMask > 0.5) {
      float inside = (vImg.x < 0.0 || vImg.x > 1.0 || vImg.y < 0.0 || vImg.y > 1.0)
        ? 0.0 : (1.0 - texture2D(uMask, vImg).r);
      a *= smoothstep(0.25, 0.75, inside);
    }
    if (a <= 0.01) discard;
    gl_FragColor = vec4(vec3(0.010, 0.011, 0.016), a);
  }
`;

const MATERIALS = new Map();
function fullscreenGeometry() { return new THREE.PlaneGeometry(1, 1); }

// A quad in the xy plane, given as (x0,y0)-(x1,y1), pushed into flat arrays.
function pushQuad(pos, uvs, parts, idx, x0, y0, x1, y1, part, x0b, x1b) {
  const base = pos.length / 3;
  const ax0 = x0b != null ? x0b : x0;
  const ax1 = x1b != null ? x1b : x1;
  pos.push(x0, y0, 0, x1, y0, 0, ax1, y1, 0, ax0, y1, 0);
  uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
  parts.push(part, part, part, part);
  idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

export class NightdriveVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const sp = parseFloat(urlParam('drivespeed'));
    this._forcedSpeed = isFinite(sp) ? clamp(sp, 0.05, 5) : null;
    const rn = parseFloat(urlParam('driverain'));
    this._forcedRain = isFinite(rn) ? clamp01(rn) : null;
    this._forceWipe = urlParam('drivewipe') === '1';
    const cs = parseFloat(urlParam('drivecity'));
    this._forcedCity = isFinite(cs) ? cs : null;
  }

  // One pass: its material AND its uniform objects, built once per tier and
  // then reused for ever.
  //
  // THE UNIFORM OBJECTS ARE CACHED TOO, and that is the whole point. three.js
  // resolves a material's uniform list once and keeps the object it resolved
  // against; handing the material a NEW uniforms object afterwards changes
  // nothing it reads, and material.needsUpdate does not help either - both
  // were tried and measured. The second Nightdrive of a session rendered the
  // first one's last frame, frozen, for ever: a strip of skyline moved 15.0
  // grey levels on the first init and exactly 0.0 on every one after, while
  // the value the shader was meant to be reading had moved by the same 0.65
  // each time. So nothing is ever swapped. The visualizer ADOPTS the cached
  // objects and resets the few values that update() does not write every
  // frame - see _resetUniforms.
  _pass(key, build) {
    let bundle = MATERIALS.get(this._tier);
    if (!bundle) { bundle = {}; MATERIALS.set(this._tier, bundle); }
    if (!bundle[key]) bundle[key] = build();
    return bundle[key];
  }

  // Everything the cached uniforms carry that a fresh run must not inherit.
  _resetUniforms() {
    const u = this._shared;
    u.uDist.value = 0;
    u.uCurve.value = 0;
    u.uTime.value = 0;
    u.uSpeed.value = 1;
    u.uHead.value = 1;
    u.uRain.value = 0.6;
    u.uLampFlick.value = 0;
    u.uProj.value = this._camera.projectionMatrix;
    u.uView.value = this._camera.matrixWorldInverse;
    this._skyUniforms.uLateral.value = 0;
    this._skyUniforms.uOnlyLayer.value = -1;
    this._skyUniforms.uSeed.value = this._forcedCity != null ? this._forcedCity : this._rand() * 40;
    this._overUniforms.uOverAt.value = -1e6;
    this._glassUniforms.uScene.value = null;
    this._coverU.value.set(1, 1);
    // No cabin mask any more, so nothing clips the drops or the blades: the
    // windscreen is the whole frame.
    if (this._dropUniforms) this._dropUniforms.uHasMask.value = 0;
    this._wiperUniforms.uHasMask.value = 0;
    if (this._dropUniforms) {
      this._dropUniforms.uScene.value = null;
      this._dropUniforms.uRefract.value = DROP_REFRACT;
    }
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._layout = TIER[this._tier];
    this._rand = mulberry32(
      this._forcedCity != null ? this._forcedCity : Math.floor(Math.random() * 0xffffff));

    // The car sits still and the world comes at it (see the header): the camera
    // never moves, which is what lets uDist be the single source of rhythm.
    camera.position.set(CAR_X, CAM_H, 0);
    camera.rotation.set(0, 0, 0);
    // The horizon sits where HORIZON_NDC puts it. It used to be MEASURED off
    // the cabin photograph's opening instead, so that a re-cut photo moved the
    // vanishing point with it; with no cabin there is no opening to measure
    // and the constant is the whole answer.
    this._horizonNdc = HORIZON_NDC;
    this._basePitch = -Math.atan(HORIZON_NDC * Math.tan((camera.fov * Math.PI / 180) / 2));
    this._bufferH = 1080;

    this._world = new THREE.Scene();
    this._rt = new FeedbackBuffer({ single: true, depth: true, scale: this._layout.scale });

    // --- uniforms every pass shares -----------------------------------------
    const core = this._pass('core', () => ({
      sodium: new THREE.Color(1.0, 0.62, 0.22),
      pal: [new THREE.Color(DEFAULT_PALETTE[0]), new THREE.Color(DEFAULT_PALETTE[1]),
            new THREE.Color(DEFAULT_PALETTE[2])]
    }));
    this._sodium = core.sodium;
    this._pal = core.pal;
    // The drops and the wipers used to be placed in the cabin photograph's
    // own space, so they lined up with the windscreen opening. With no
    // photograph the transform is the identity - but the holder still has to
    // exist, because both passes share it and three.js binds the object once.
    this._coverU = this._pass('cover', () => ({ cover: { value: new THREE.Vector2(1, 1) } })).cover;
    const u = this._pass('shared', () => ({ shared: {
      uDist: { value: 0 },
      uCurve: { value: 0 },
      uTime: { value: 0 },
      uSpeed: { value: 1 },
      uHead: { value: 1 },
      uRain: { value: 0.6 },
      uWet: { value: this._layout.wetRoad ? 1 : 0 },
      uSodium: { value: this._sodium },
      uLampFlick: { value: 0 },
      uProj: { value: camera.projectionMatrix },
      uView: { value: camera.matrixWorldInverse }
    } })).shared;
    this._shared = u;

    this._buildSky();
    this._buildRoad();
    this._buildLamps();
    this._buildRail();
    this._buildRain();
    this._buildOverpass();
    this._buildGlass();
    this._buildDrops();
    this._buildWipers();
    this._resetUniforms();

    // --- state ---
    this._time = 0;
    this._dist = 0;
    this._speed = DASH_PERIOD / (60 / NO_BPM);
    this._speedScale = 1;
    this._stopFactor = 1;
    this._paused = false;
    this._curve = 0;
    this._curveTarget = 0;
    this._lateral = 0;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._rainLull = 1;
    this._lullUntil = 0;
    this._lastPhraseIndex = null;
    this._lastBarIndex = null;
    this._lastTrackId = undefined;
    this._lastPaletteRef = undefined;
    this._overAt = -1e6;
    this._phraseCount = 0;
    this._wiperPrevL = -WIPER_PARK;
    this._wiperPrevR = WIPER_PARK;
    this._wipeManual = 0;
  }

  // --------------------------------------------------------------------------
  // World passes
  // --------------------------------------------------------------------------
  _buildSky() {
    const P = this._pass('sky', () => {
      const uniforms = {
      uHorizon: { value: HORIZON_NDC },
      uAspect: { value: 1.778 },
      uLateral: { value: 0 },
      uOnlyLayer: { value: -1 },
      uSeed: { value: 0 },
      uCityDensity: { value: 0.4 },
      uHigh: { value: 0 },
      uTime: this._shared.uTime,
      uPal0: { value: this._pal[0] },
      uPal1: { value: this._pal[1] },
      uPal2: { value: this._pal[2] },
      uSodium: this._shared.uSodium,
      uLayers: { value: this._layout.cityLayers }
    };
      return { uniforms, material: new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: FULLSCREEN_VS, fragmentShader: bake(SKY_FS),
    depthTest: false, depthWrite: false
      }) };
    });
    this._skyUniforms = P.uniforms;
    this._skyMat = P.material;

    this._sky = new THREE.Mesh(fullscreenGeometry(), this._skyMat);
    this._sky.frustumCulled = false;
    this._sky.renderOrder = 0;
    this._world.add(this._sky);
  }

  _buildRoad() {
    const P = this._pass('road', () => {
      const uniforms = Object.assign({}, this._shared, {
      uTan: { value: new THREE.Vector2(1, 0.67) },
      uCamRot: { value: new THREE.Matrix3() },
      uCamPos: { value: new THREE.Vector3(CAR_X, CAM_H, 0) },
      uRoadWet: { value: this._layout.wetRoad ? 1 : 0.25 },
      uGrain: { value: 2.0 / 255 }
    });
      return { uniforms, material: new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: FULLSCREEN_VS, fragmentShader: bake(ROAD_FS),
    depthTest: false, depthWrite: false
      }) };
    });
    this._roadUniforms = P.uniforms;
    this._roadMat = P.material;

    this._road = new THREE.Mesh(fullscreenGeometry(), this._roadMat);
    this._road.frustumCulled = false;
    this._road.renderOrder = 1;
    this._world.add(this._road);
  }

  _buildLamps() {
    const pos = [], uvs = [], parts = [], idx = [];
    const ARM = LAMP_ARM;                      // the arm reaches over the shoulder
    pushQuad(pos, uvs, parts, idx, -0.012, 0, 0.012, LAMP_H, 0);                    // post
    pushQuad(pos, uvs, parts, idx, 0, LAMP_H - 0.022, ARM, LAMP_H + 0.012, 0);      // arm
    pushQuad(pos, uvs, parts, idx, ARM - 0.105, LAMP_H - 0.10, ARM + 0.105, LAMP_H + 0.05, 1);
    // The cone. pushQuad's optional pair is the TOP edge, and the top of this
    // quad is the head - so the narrow pair goes there and the wide one is the
    // default, at the road.
    pushQuad(pos, uvs, parts, idx, ARM - 1.05, 0, ARM + 1.05, LAMP_H - 0.09, 2,
             ARM - 0.09, ARM + 0.09);
    const base = new THREE.InstancedBufferGeometry();
    base.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    base.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    base.setAttribute('aPart', new THREE.Float32BufferAttribute(parts, 1));
    base.setIndex(idx);
    const n = LAMP_COUNT * 2;
    const side = new Float32Array(n), index = new Float32Array(n);
    for (let k = 0; k < LAMP_COUNT; k++) {
      side[k * 2] = -1; index[k * 2] = k;
      side[k * 2 + 1] = 1; index[k * 2 + 1] = k;
    }
    base.setAttribute('aSide', new THREE.InstancedBufferAttribute(side, 1));
    base.setAttribute('aIndex', new THREE.InstancedBufferAttribute(index, 1));
    base.instanceCount = n;
    base.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._lampMat = this._pass('lamp', () => ({
      uniforms: this._shared, material: new THREE.ShaderMaterial({
        uniforms: this._shared, vertexShader: bake(LAMP_VS), fragmentShader: bake(LAMP_FS),
        blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
      })
    })).material;
    this._lamps = new THREE.Mesh(base, this._lampMat);
    this._lamps.frustumCulled = false;
    this._lamps.renderOrder = 3;
    this._world.add(this._lamps);
  }

  _buildRail() {
    const pos = [], uvs = [], parts = [], idx = [];
    pushQuad(pos, uvs, parts, idx, -0.5, RAIL_Y - 0.055, 0.5, RAIL_Y + 0.055, 0);
    pushQuad(pos, uvs, parts, idx, -0.13, RAIL_Y - 0.020, 0.13, RAIL_Y + 0.020, 1);
    const base = new THREE.InstancedBufferGeometry();
    base.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    base.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    base.setAttribute('aPart', new THREE.Float32BufferAttribute(parts, 1));
    base.setIndex(idx);
    const index = new Float32Array(RAIL_COUNT);
    for (let k = 0; k < RAIL_COUNT; k++) index[k] = k;
    base.setAttribute('aIndex', new THREE.InstancedBufferAttribute(index, 1));
    base.instanceCount = RAIL_COUNT;
    base.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._railMat = this._pass('rail', () => ({
      uniforms: this._shared, material: new THREE.ShaderMaterial({
        uniforms: this._shared, vertexShader: bake(RAIL_VS), fragmentShader: bake(RAIL_FS),
        blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false,
        // DOUBLE-SIDED, and this is not a detail: the rail's quads lie in the
        // x = const plane, so their winding as seen from a camera on the road's
        // axis is the opposite of every other quad in this file. Back-face
        // culled, the guardrail rendered exactly nothing - measured at zero lit
        // pixels, which is what sent me looking for it.
        side: THREE.DoubleSide
      })
    })).material;
    this._rail = new THREE.Mesh(base, this._railMat);
    this._rail.frustumCulled = false;
    this._rail.renderOrder = 2;
    this._world.add(this._rail);
  }

  _buildRain() {
    const n = this._layout.rain;
    const seeds = new Float32Array(n * 2), ends = new Float32Array(n * 2);
    const zero = new Float32Array(n * 2 * 3);
    for (let i = 0; i < n; i++) {
      const s = this._rand() * 97 + 1;
      seeds[i * 2] = s; seeds[i * 2 + 1] = s;
      ends[i * 2] = 0; ends[i * 2 + 1] = 1;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(zero, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    geo.setAttribute('aEnd', new THREE.BufferAttribute(ends, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._rainMat = this._pass('rain', () => ({
      uniforms: this._shared, material: new THREE.ShaderMaterial({
        uniforms: this._shared, vertexShader: bake(RAIN_VS), fragmentShader: bake(RAIN_FS),
        blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
      })
    })).material;
    this._rainLines = new THREE.LineSegments(geo, this._rainMat);
    this._rainLines.frustumCulled = false;
    this._rainLines.renderOrder = 4;
    this._world.add(this._rainLines);
  }

  _buildOverpass() {
    const P = this._pass('over', () => {
      const uniforms = Object.assign({}, this._shared, { uOverAt: { value: -1e6 } });
      return { uniforms, material: new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: bake(OVER_VS), fragmentShader: bake(OVER_FS),
    transparent: true, depthTest: false, depthWrite: false
      }) };
    });
    this._overUniforms = P.uniforms;
    this._overMat = P.material;

    this._over = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._overMat);
    this._over.frustumCulled = false;
    this._over.renderOrder = 5;
    this._world.add(this._over);
  }

  // --------------------------------------------------------------------------
  // The windscreen, in the shared scene: the glass itself, the water on it,
  // and the blades.
  // --------------------------------------------------------------------------
  _buildGlass() {
    const P = this._pass('glass', () => {
      const uniforms = {
        uScene: { value: null },
        uAspect: { value: 1.778 },
        uWiperAngle: { value: new THREE.Vector2(-WIPER_PARK, WIPER_PARK) },
        uPivotL: { value: new THREE.Vector2(-WIPER_PIVOT_X, WIPER_PIVOT_Y) },
        uPivotR: { value: new THREE.Vector2(WIPER_PIVOT_X, WIPER_PIVOT_Y) },
        uFilm: { value: 0.6 },
        uTime: this._shared.uTime,
        uSafeCenter: safeZoneUniforms.uSafeCenter,
        uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
        uSafeFeather: safeZoneUniforms.uSafeFeather
      };
      return { uniforms, material: new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: FULLSCREEN_VS, fragmentShader: bake(GLASS_FS),
        depthTest: false, depthWrite: false
      }) };
    });
    this._glassUniforms = P.uniforms;
    this._glassMat = P.material;
    this._glass = new THREE.Mesh(fullscreenGeometry(), this._glassMat);
    this._glass.frustumCulled = false;
    this._glass.renderOrder = -10;
    const self = this;
    this._glass.onBeforeRender = function (renderer) { self._renderWorld(renderer); };
    this._scene.add(this._glass);
  }

  _buildDrops() {
    const n = this._layout.drops;
    this._dropCount = n;
    if (!n) { this._drops = null; return; }
    this._dx = new Float32Array(n);
    this._dy = new Float32Array(n);
    this._dr = new Float32Array(n);
    this._dvx = new Float32Array(n);
    this._dvy = new Float32Array(n);
    this._dtrail = new Float32Array(n);
    this._dfade = new Float32Array(n);
    this._dwait = new Float32Array(n);
    this._dropPos = new Float32Array(n * 2);
    this._dropSize = new Float32Array(n);
    this._dropTrail = new Float32Array(n);
    this._dropFade = new Float32Array(n);
    for (let i = 0; i < n; i++) this._spawnDrop(i, true);

    const base = new THREE.InstancedBufferGeometry();
    const plane = new THREE.PlaneGeometry(1, 1);
    base.setAttribute('position', plane.getAttribute('position'));
    base.setAttribute('uv', plane.getAttribute('uv'));
    base.setIndex(plane.getIndex());
    plane.dispose();
    this._aDropPos = new THREE.InstancedBufferAttribute(this._dropPos, 2);
    this._aDropSize = new THREE.InstancedBufferAttribute(this._dropSize, 1);
    this._aDropTrail = new THREE.InstancedBufferAttribute(this._dropTrail, 1);
    this._aDropFade = new THREE.InstancedBufferAttribute(this._dropFade, 1);
    for (const a of [this._aDropPos, this._aDropSize, this._aDropTrail, this._aDropFade]) {
      a.setUsage(THREE.DynamicDrawUsage);
    }
    base.setAttribute('aPos', this._aDropPos);
    base.setAttribute('aSize', this._aDropSize);
    base.setAttribute('aTrail', this._aDropTrail);
    base.setAttribute('aFade', this._aDropFade);
    base.instanceCount = n;
    base.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);

    const P = this._pass('drop', () => {
      const uniforms = {
        uScene: { value: null },
        uMask: { value: null },
        uHasMask: { value: 0 },
        uCover: this._coverU,
        uAspect: this._glassUniforms.uAspect,
        uRefract: { value: DROP_REFRACT }
      };
      return { uniforms, material: new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: bake(DROP_VS), fragmentShader: bake(DROP_FS),
        transparent: true, depthTest: false, depthWrite: false
      }) };
    });
    this._dropUniforms = P.uniforms;
    this._dropMat = P.material;
    this._drops = new THREE.Mesh(base, this._dropMat);
    this._drops.frustumCulled = false;
    this._drops.renderOrder = 0;
    this._scene.add(this._drops);
  }

  _buildWipers() {
    const pos = [], uvs = [], which = [], idx = [];
    for (let w = 0; w < 2; w++) {
      const b = pos.length / 3;
      pos.push(-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0);
      uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
      which.push(w, w, w, w);
      idx.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setAttribute('aWiper', new THREE.Float32BufferAttribute(which, 1));
    geo.setIndex(idx);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    const P = this._pass('wiper', () => {
      const uniforms = {
        uWiperAngle: this._glassUniforms.uWiperAngle,
        uPivotL: this._glassUniforms.uPivotL,
        uPivotR: this._glassUniforms.uPivotR,
        uAspect: this._glassUniforms.uAspect,
        uMask: { value: null },
        uHasMask: { value: 0 },
        uCover: this._coverU
      };
      return { uniforms, material: new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: bake(WIPER_VS), fragmentShader: bake(WIPER_FS),
        transparent: true, depthTest: false, depthWrite: false
      }) };
    });
    this._wiperUniforms = P.uniforms;
    this._wiperMat = P.material;
    this._wipers = new THREE.Mesh(geo, this._wiperMat);
    this._wipers.frustumCulled = false;
    this._wipers.renderOrder = 5;
    this._scene.add(this._wipers);
  }

  _renderWorld(renderer) {
    this._bufferH = Math.max(1, renderer.getDrawingBufferSize(_scratchSize).y);
    this._rt.ensureSize(renderer);
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._rt.read);
    renderer.clear(true, true, true);
    renderer.render(this._world, this._camera);
    renderer.setRenderTarget(prev);
    const tex = this._rt.read.texture;
    this._glassUniforms.uScene.value = tex;
    if (this._dropUniforms) this._dropUniforms.uScene.value = tex;
  }

  // --------------------------------------------------------------------------
  // The drops. Their motion is the airflow over the screen: the faster the car
  // goes the harder they are driven up and out, and a drop that has moved is a
  // drop with a tail. The wipers delete them, which is why this is on the CPU.
  // --------------------------------------------------------------------------
  _spawnDrop(i, initial) {
    const r = this._rand;
    const aspect = this._glassUniforms ? this._glassUniforms.uAspect.value : 1.778;
    this._dx[i] = (r() * 2 - 1) * aspect * 1.02;
    this._dy[i] = -1.0 + r() * 2.0;
    // Small drops dominate; the few big ones are what actually run.
    const t = r();
    this._dr[i] = 0.012 + 0.042 * t * t;
    this._dvx[i] = 0;
    this._dvy[i] = 0;
    this._dtrail[i] = 0;
    this._dfade[i] = initial ? 1 : 0;
    this._dwait[i] = 0;
  }

  _updateDrops(dt, wipeL0, wipeL1, wipeR0, wipeR1) {
    if (!this._dropCount) return;
    const aspect = this._glassUniforms.uAspect.value;
    const ref = DASH_PERIOD / (60 / NO_BPM);
    const wind = clamp(this._speed / ref, 0, 2.2);
    const density = this._dropUniforms ? this._rainDensity : 1;
    const pivots = [
      [-WIPER_PIVOT_X, WIPER_PIVOT_Y, wipeL0, wipeL1],
      [WIPER_PIVOT_X, WIPER_PIVOT_Y, wipeR0, wipeR1]
    ];
    for (let i = 0; i < this._dropCount; i++) {
      if (this._dwait[i] > 0) {
        this._dwait[i] -= dt;
        if (this._dwait[i] <= 0) this._spawnDrop(i, false);
        this._dropFade[i] = 0;
        continue;
      }
      // Driven up and outward. Bigger drops catch more wind, which is why a
      // windscreen at speed sorts itself into long runners and a fine mist
      // that barely moves at all. Measured and then cut by a factor of six:
      // at the first calibration a drop crossed the glass in two seconds,
      // which reads as an insect, not as water.
      const drive = (0.015 + this._dr[i] * 4.8) * wind;
      this._dvy[i] += drive * dt * 1.5;
      this._dvx[i] += (this._dx[i] >= 0 ? 1 : -1) * drive * dt * 0.8;
      const damp = Math.exp(-dt * 2.4);
      this._dvx[i] *= damp;
      this._dvy[i] *= damp;
      const mx = this._dvx[i] * dt, my = this._dvy[i] * dt;
      this._dx[i] += mx;
      this._dy[i] += my;
      const moved = Math.sqrt(mx * mx + my * my);
      this._dtrail[i] = Math.min(0.5, this._dtrail[i] * Math.exp(-dt * 1.2) + moved * 26.0);

      // Wiped? The blade swept across this drop between the previous frame and
      // this one if its bearing from the pivot lies inside the arc the blade
      // covered, and it is within the blade's reach.
      let wiped = false, smear = 0;
      for (let w = 0; w < 2; w++) {
        const px = pivots[w][0], py = pivots[w][1], a0 = pivots[w][2], a1 = pivots[w][3];
        const ddx = this._dx[i] - px, ddy = this._dy[i] - py;
        const rr = Math.sqrt(ddx * ddx + ddy * ddy);
        if (rr < WIPER_INNER || rr > WIPER_LEN) continue;
        const a = Math.atan2(ddx, ddy);
        const lo = Math.min(a0, a1) - 0.035, hi = Math.max(a0, a1) + 0.035;
        if (a >= lo && a <= hi) { wiped = true; break; }
        smear = Math.max(smear, 1 - Math.min(Math.abs(a - a1) / 0.22, 1));
      }
      if (wiped) {
        // Gone, and the glass stays clear for a moment before the rain puts a
        // new one there - longer when it is barely raining.
        this._dfade[i] = 0;
        this._dwait[i] = 0.12 + this._rand() * (0.5 + 2.4 * (1 - density));
        this._dropFade[i] = 0;
        continue;
      }
      // Under the blade but not yet swept: the water is dragged out of shape.
      if (smear > 0) this._dtrail[i] = Math.min(0.5, this._dtrail[i] + smear * 0.30);

      if (Math.abs(this._dx[i]) > aspect * 1.06 || this._dy[i] > 1.08) {
        this._spawnDrop(i, false);
      }
      this._dfade[i] = Math.min(1, this._dfade[i] + dt * 3.2);
      this._dropPos[i * 2] = this._dx[i];
      this._dropPos[i * 2 + 1] = this._dy[i];
      this._dropSize[i] = this._dr[i];
      this._dropTrail[i] = this._dtrail[i];
      this._dropFade[i] = this._dfade[i] * clamp01(0.25 + density * 1.3);
    }
    this._aDropPos.needsUpdate = true;
    this._aDropSize.needsUpdate = true;
    this._aDropTrail.needsUpdate = true;
    this._aDropFade.needsUpdate = true;
  }

  // One full sweep and return inside the bar, with the arm settling rather
  // than stopping dead at each end - a wiper is a sprung arm and that little
  // bounce is most of what makes it read as mechanical.
  _wiperSweep(p) {
    let s;
    if (p < 0.42) s = p / 0.42;
    else if (p < 0.84) s = 1 - (p - 0.42) / 0.42;
    else return 0;
    const e = s * s * (3 - 2 * s);
    const end = Math.min(s, 1 - s);
    const jerk = Math.exp(-end * 30) * Math.sin(end * 110) * 0.04;
    return clamp(e + jerk, -0.02, 1.02);
  }

  // NO hideOverlay. It used to be true, because the cover and the title were
  // printed on the car's own dashboard screen and the DOM overlay would have
  // been the same two things a second time, floating in front of the
  // windscreen. With the cabin gone that screen went with it, so the standard
  // centre overlay is the only place they can be - and the safe zone comes
  // back on with it, which is why the glass pass still reads it.

  onTrackChange() {
    this._lastPaletteRef = undefined;
    // A new track is a new town: the skyline is reseeded unless a seed was
    // forced on the URL.
    if (this._forcedCity == null && this._skyUniforms) {
      this._skyUniforms.uSeed.value = Math.random() * 40;
    }
  }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    const u = this._shared;
    u.uTime.value = this._time % 600;

    // --- pause: a car does not freeze, it comes to a stop ---------------------
    const playing = profile.playbackState === 'PLAYING';
    const want = playing ? 1 : 0;
    const rate = dt / STOP_SECONDS;
    this._stopFactor = want > this._stopFactor
      ? Math.min(1, this._stopFactor + rate * 2.5)
      : Math.max(0, this._stopFactor - rate);

    const director = profile.director;
    const bands = director && director.bands;
    // ABSENT IS NOT ZERO: without an analysis timeline director.bands is
    // {0,0,0} rather than null, and read literally that is "no bass, no rain,
    // no city lights" for the whole of any radio stream.
    const graded = !!(director && director.hasReliableGrid) && bands;
    const low = graded && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = graded && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = graded && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-dt / 0.22);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;

    // --- palette ---------------------------------------------------------
    // The title, the artist and the sleeve are no longer read here at all:
    // they went to the dashboard screen, and the DOM overlay draws them now.
    // Only the three vivid colours are still wanted, for the city and the
    // rain.
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      const src = (vivid && vivid.length) ? vivid : DEFAULT_PALETTE;
      for (let i = 0; i < 3; i++) this._pal[i].set(src[i % src.length]);
    }
    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      this.onTrackChange();
    }

    // --- phrases: the road bends, the speed drifts, sometimes a bridge --------
    const phraseIndex = profile.phraseIndex;
    const newPhrase = phraseIndex != null
      ? (this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex)
      : (this._time > (this._noGridPhraseAt || 0));
    if (newPhrase) {
      if (phraseIndex == null) this._noGridPhraseAt = this._time + 9.6;
      this._phraseCount++;
      this._curveTarget = (this._rand() * 2 - 1) * CURVE_MAX;
      this._speedWobbleTarget = 1 + (this._rand() * 2 - 1) * SPEED_WOBBLE;
      // Rare, and only on a phrase: four or five of them in a track.
      if (this._rand() < 0.22 && this._dist > this._overAt + 60) this._overAt = this._dist + 34;
      // Rain comes and goes rather than raining evenly for four minutes.
      if (this._rand() < 0.3) this._lullUntil = this._time + 4 + this._rand() * 8;
    }
    if (phraseIndex != null) this._lastPhraseIndex = phraseIndex;
    this._curve += (this._curveTarget - this._curve) * (1 - Math.exp(-dt / 2.6));
    const wobT = this._speedWobbleTarget != null ? this._speedWobbleTarget : 1;
    this._speedWobble = (this._speedWobble || 1) + (wobT - (this._speedWobble || 1)) * (1 - Math.exp(-dt / 1.8));

    // --- bars ------------------------------------------------------------------
    // There was a pitch impulse here, the cabin taking a joint in the road on
    // every bar. It has been removed: an impulse is a STEP IN VELOCITY, and a
    // step in velocity is a jolt however well damped the spring after it is.
    // Measured, it moved the camera 0.37 milliradians in a single frame against
    // a 0.0001 median - a 2600x spike, once a bar, on the downbeat. The road's
    // own advance was already perfectly smooth (zero frames off its median), so
    // this was the whole of what could be felt.
    const barIndex = profile.barIndex;
    if (barIndex != null) this._lastBarIndex = barIndex;

    // --- the one number everything on the road is made of ---------------------
    this._advance(dt, profile);

    // --- camera ---------------------------------------------------------------
    const cam = this._camera;
    cam.position.set(CAR_X, CAM_H, 0);
    cam.rotation.set(this._basePitch, 0, -this._curve * 5.5);
    cam.updateMatrixWorld(true);
    const aspect = cam.aspect || 1.778;
    const tanY = Math.tan((cam.fov * Math.PI / 180) / 2);
    this._roadUniforms.uTan.value.set(tanY * aspect, tanY);
    this._roadUniforms.uCamRot.value.setFromMatrix4(cam.matrixWorld);
    this._roadUniforms.uCamPos.value.set(CAR_X, CAM_H, 0);
    // How far the car has moved sideways relative to the skyline: mostly the
    // bend, plus a baseline so a dead-straight stretch is never frozen.
    this._lateral += (this._curve * 4.0 + CITY_DRIFT) * this._speed * dt;
    this._skyUniforms.uLateral.value = this._lateral;
    this._skyUniforms.uAspect.value = aspect;
    this._skyUniforms.uHorizon.value = this._horizonNdc;
    this._glassUniforms.uAspect.value = aspect;

    // --- lights, rain, city ----------------------------------------------------
    u.uCurve.value = this._curve;
    u.uHead.value = (0.72 + 0.55 * this._lowSmooth) * (0.35 + 0.65 * this._stopFactor);
    const lull = this._time < this._lullUntil ? 0.12 : 1;
    this._rainLull += (lull - this._rainLull) * (1 - Math.exp(-dt / 1.6));
    this._rainDensity = this._forcedRain != null
      ? this._forcedRain
      : clamp01(0.22 + 0.78 * this._midSmooth) * this._rainLull;
    u.uRain.value = this._rainDensity;
    u.uSpeed.value = this._speed / (DASH_PERIOD / (60 / NO_BPM));
    u.uLampFlick.value = 0.32;
    this._overUniforms.uOverAt.value = this._overAt;

    const progress = profile.duration > 0 ? clamp01(profile.progress)
      : (director && director.intensity != null ? director.intensity : 0.5);
    // Out of town, into the centre, back out again.
    this._skyUniforms.uCityDensity.value = 0.14 + 0.72 * Math.sin(Math.PI * progress);
    this._skyUniforms.uHigh.value = this._highSmooth;
    this._glassUniforms.uFilm.value = 0.25 + 0.65 * this._rainDensity;

    // --- wipers, on the same phase as the lamps -------------------------------
    // fract(uDist / LAMP_SPACING) IS barPhase (see _advance), so the wipers are
    // bar-locked by construction - and they slow to a stop with the car,
    // because the car is the only clock they have.
    let wp = (this._dist / LAMP_SPACING) % 1;
    if (this._forceWipe) { this._forceWipe = false; this._wipeManual = 1; }
    if (this._wipeManual > 0) {
      this._wipeManual = Math.max(0, this._wipeManual - dt / 1.1);
      wp = 1 - this._wipeManual;
    }
    const s = this._wiperSweep(wp);
    const aL = -WIPER_PARK + s * WIPER_SWEEP;
    const aR = WIPER_PARK - s * WIPER_SWEEP;
    const pL = this._wiperPrevL, pR = this._wiperPrevR;
    this._glassUniforms.uWiperAngle.value.set(aL, aR);
    this._updateDrops(dt, pL, aL, pR, aR);
    this._wiperPrevL = aL;
    this._wiperPrevR = aR;
  }

  // The phase-locked loop. uDist advances at whatever speed the tempo and the
  // phrase wobble ask for, and is then pulled - never jumped - toward the grid.
  _advance(dt, profile) {
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    let v = DASH_PERIOD / beatS;
    if (this._forcedSpeed != null) v = (DASH_PERIOD / (60 / NO_BPM)) * this._forcedSpeed;
    else v *= this._speedWobble;
    v *= this._stopFactor;
    this._speed = v;
    this._dist += v * dt;

    const barPhase = profile.barPhase;
    if (barPhase != null && this._forcedSpeed == null && this._stopFactor > 0.85) {
      const have = (this._dist / LAMP_SPACING) % 1;
      const err = phaseError(barPhase, have);
      this._dist += err * LAMP_SPACING * Math.min(1, dt / PLL_TAU);
    }
    this._shared.uDist.value = this._dist;
  }

  dispose() {
    for (const m of [this._glass, this._drops, this._wipers]) {
      if (m) this._scene.remove(m);
    }
    for (const m of [this._sky, this._road, this._lamps, this._rail, this._rainLines, this._over]) {
      if (m) { this._world.remove(m); m.geometry.dispose(); }
    }
    if (this._glass) this._glass.geometry.dispose();
    if (this._drops) this._drops.geometry.dispose();
    if (this._wipers) this._wipers.geometry.dispose();
    this._rt.dispose();
    // The MATERIALS are deliberately NOT disposed - see the cache above.
    this._glassUniforms = null;
  }
}
